import {
  EventCardActions,
  EventCardBadge,
  EventCardDate,
  EventCardDetails,
  EventCardDetailsSpacer,
  EventCardImage,
  EventCardLink,
  EventCardLocation,
  EventCardPrice,
  EventCardPromoted,
  EventCardSaveAction,
  EventCardShareAction,
  EventCardTitle,
  EventCardTracking,
  EventCardUrgencySignals,
  getEventCardLinkProps,
  getFormattedSalesStatus,
  getShareActionProps,
  HORIZONTAL_CARD_BADGE_STYLE,
  HORIZONTAL_LEFT_CARD_BADGE_STYLE,
  useFormattedLocationString,
  useGetFormattedEventDateTimeString,
  useMinPriceString,
  VerticalEventCard,
  type FormattedEvent,
} from '@eventbrite/consumer-search.event-renderer';
import { VERTICAL_EVENT_CARD_IMAGE_CONFIG } from '@eventbrite/consumer-search.event-renderer/constants';
import { BoltIcon } from '@eventbrite/marmalade';
import { defineMessages, useIntl } from 'react-intl';

import { getEventCardSignalsVisibility } from '../getEventCardSignalsVisibility';
import styles from './OrganizerVerticalEventCard.module.scss';

const messages = defineMessages({
  highlight: {
    id: 'HIGHLIGHT',
    defaultMessage: 'HIGHLIGHT',
  },
  viewEvent: {
    id: 'View {eventName}',
    defaultMessage: 'View {eventName}',
  },
});

interface Props {
  event: FormattedEvent;
  locale: string;
  statsigLocationString: string;
  bucketLabel: string;
  isAuthenticated: boolean;
  isLazyImage?: boolean;
  affCode?: string;
  showSaveAction?: boolean;
  showShareAction?: boolean;
  isHighlighted?: boolean;
  hasPinAction?: boolean;
  shareOptions?: {
    utmOptions?: Record<string, string>;
  };
}

export function OrganizerVerticalEventCard({
  event,
  locale,
  statsigLocationString,
  bucketLabel,
  isAuthenticated,
  isLazyImage = false,
  affCode = 'ebdsoporgprofile',
  showSaveAction = false,
  showShareAction = false,
  isHighlighted = false,
  hasPinAction = false,
  shareOptions = {},
}: Props) {
  const intl = useIntl();
  const { utmOptions } = shareOptions;
  const { showUrgencySignals } = getEventCardSignalsVisibility({
    event,
    withLimitedVisibility: false,
  });
  const linkProps = getEventCardLinkProps({
    affCode,
    event,
    label: intl.formatMessage(messages.viewEvent, { eventName: event.name }),
  });
  const formattedDateString = useGetFormattedEventDateTimeString({
    startDate: event.startDate,
    startTime: event.startTime,
    timezone: event.timezone,
    additionalSessionsCount: event.additionalSessionsCount,
    locale,
    useUserTimezone: event.isOnlineEvent,
    dateTimeDelimeter: '\u2022',
  });
  const formattedPriceRangeString = useMinPriceString({ price: event.minPrice, locale });
  const formattedLocationString = useFormattedLocationString({
    venue: event.venue,
    style: 'venue',
  });
  const formattedSalesStatus = getFormattedSalesStatus(event);
  const shouldShowUrgencySignals =
    !!event.urgencySignals && !formattedSalesStatus && showUrgencySignals;

  return (
    <section className="discover-vertical-event-card">
      <VerticalEventCard actionVisibility="hidden">
        <EventCardTracking
          statsigLocationString={statsigLocationString}
          event={event}
          eventBucketLabel={bucketLabel}
        >
          <EventCardBadge
            style={hasPinAction ? HORIZONTAL_CARD_BADGE_STYLE : HORIZONTAL_LEFT_CARD_BADGE_STYLE}
          >
            {formattedSalesStatus}
          </EventCardBadge>
          <EventCardLink {...linkProps}>
            <EventCardImage
              name={event.name}
              loading={isLazyImage ? 'lazy' : 'eager'}
              src={event.imageUrl}
              imageSizes={event.imageSizes}
              tokens={{
                ...VERTICAL_EVENT_CARD_IMAGE_CONFIG,
                '--image-background-color': event.edgeColor,
                '--image-object-position': event.focalPoint
                  ? `${event.focalPoint.x * 100}% ${event.focalPoint.y * 100}%`
                  : undefined,
              }}
            />
          </EventCardLink>
          <EventCardDetails
            space="xxs-spacing"
            tokens={{
              '--EventCardDetailsPadding': '8px 8px 16px 12px',
              '--EventCardDetailsFlexGrow': '2',
              '--EventCardDetailsPosition': 'relative',
            }}
          >
            {isHighlighted ? (
              <div className={styles.highlightLabel}>
                <BoltIcon size="xsmall" aria-hidden />
                <span>{intl.formatMessage(messages.highlight)}</span>
              </div>
            ) : null}
            {shouldShowUrgencySignals ? (
              <EventCardUrgencySignals
                className={styles.urgencySignals}
                urgencySignals={event.urgencySignals}
              />
            ) : null}
            <EventCardLink {...linkProps}>
              <EventCardTitle
                color="neutral-800"
                className="event-card__clamp-line--two"
                title={event.name}
              />
            </EventCardLink>
            <EventCardDate
              color="neutral-800"
              formattedDateString={formattedDateString}
              variant="body-md-bold"
            />
            <EventCardLocation
              className="event-card__clamp-line--one"
              formattedLocationString={formattedLocationString}
              variant="body-md"
            />
            <EventCardDetailsSpacer />
            <div className={styles.priceWrapper}>
              <EventCardPrice
                color="neutral-800"
                isLoading={false}
                isFree={event.isFree}
                formattedPriceString={formattedPriceRangeString}
                variant="body-md-bold"
              />
            </div>
            <EventCardPromoted
              className={styles.promotedLabel}
              isPromoted={event.isPromoted}
              withUpsellModal={false}
            />
            {showSaveAction || showShareAction ? (
              <EventCardActions
                tokens={{
                  '--EventCardActionsPosition': 'absolute',
                  '--EventCardActionsTop': '-48px',
                  '--EventCardActionsRight': '8px',
                  '--EventCardActionsGap': '8px',
                }}
                actionStyleOverride="outline"
              >
                {showSaveAction ? (
                  <EventCardSaveAction
                    isAuthenticated={isAuthenticated}
                    savedByYou={event.savedByYou}
                    id={event.id}
                    eventName={event.name}
                    imageUrl={event.imageUrl}
                  />
                ) : null}
                {showShareAction ? (
                  <EventCardShareAction
                    {...getShareActionProps({
                      name: event.name,
                      id: event.id,
                      url: event.url,
                      affCode,
                      utmOptions,
                    })}
                  />
                ) : null}
              </EventCardActions>
            ) : null}
          </EventCardDetails>
        </EventCardTracking>
      </VerticalEventCard>
    </section>
  );
}
